import React, { useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { sourceChips } from '../utils/previewSources';
import {
  Box,
  Typography,
  Divider,
  Chip,
  Paper,
  Alert,
} from '@mui/material';
import {
  ConfirmationNumber as TicketIcon,
  Email as EmailIcon,
  Campaign as SnsIcon,
  Psychology as EnrichmentIcon,
} from '@mui/icons-material';
import type { PreviewRuleOutputResponse, PreviewChannelResult } from '../../../types/backend';
import SeverityBadge from '../../../shared/components/SeverityBadge';
import PreviewRailShell from './PreviewRailShell';

interface PreviewRailProps {
  onPreview: () => Promise<PreviewRuleOutputResponse>;
  hasSampleEvent: boolean;
}

const CHANNEL_ICONS: Record<string, React.ReactElement> = {
  itsm: <TicketIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
  sns: <SnsIcon fontSize="small" />,
};

const CHANNEL_LABELS: Record<string, string> = {
  itsm: 'Support ticket',
  email: 'Email',
  sns: 'SNS topic',
};

const ItsmOutput: React.FC<{ html: string }> = ({ html }) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      maxHeight: 300,
      overflowY: 'auto',
    }}
  >
    <Box
      sx={{
        p: 1.5,
        fontSize: '0.8rem',
        lineHeight: 1.5,
        '& *': { maxWidth: '100%' },
        '& img': { display: 'none' },
      }}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  </Box>
);

const EmailOutput: React.FC<{ output: Record<string, string> | string }> = ({ output }) => {
  const subject = typeof output === 'object' ? output.subject : undefined;
  const body = typeof output === 'object' ? output.body : String(output);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {subject !== undefined && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
            Subject
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {subject}
          </Typography>
        </Box>
      )}
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
          Body
        </Typography>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 200,
            overflowY: 'auto',
            fontSize: '0.8rem',
            color: 'text.secondary',
          }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

const SnsOutput: React.FC<{ output: unknown }> = ({ output }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.5,
      bgcolor: 'action.hover',
      borderRadius: 1,
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      overflowX: 'auto',
      maxHeight: 200,
      overflowY: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
    }}
  >
    {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
  </Box>
);

const ChannelCard: React.FC<{ result: PreviewChannelResult }> = ({ result }) => {
  const icon = CHANNEL_ICONS[result.channel] ?? <TicketIcon fontSize="small" />;
  const label = CHANNEL_LABELS[result.channel] ?? result.channel;
  const chips = sourceChips(result.sources);

  const renderOutput = () => {
    if (result.channel === 'itsm') {
      const html = typeof result.rendered_output === 'string'
        ? result.rendered_output
        : JSON.stringify(result.rendered_output, null, 2);
      return <ItsmOutput html={html} />;
    }
    if (result.channel === 'email') {
      return <EmailOutput output={result.rendered_output} />;
    }
    return <SnsOutput output={result.rendered_output} />;
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {icon}
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Box sx={{ flex: 1 }} />
        {chips.map((chip) => (
          <Chip key={chip} label={chip} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        ))}
      </Box>
      {renderOutput()}
    </Paper>
  );
};

const PreviewRail: React.FC<PreviewRailProps> = ({ onPreview, hasSampleEvent }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PreviewRuleOutputResponse | null>(null);

  const handleRun = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await onPreview();
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [onPreview]);

  const enrichment = result?.enrichment as Record<string, string> | undefined;

  return (
    <PreviewRailShell
      runLabel="Test with sample"
      runningLabel="Running…"
      running={loading}
      runDisabled={!hasSampleEvent}
      onRun={handleRun}
      hasSample={hasSampleEvent}
      noSampleMessage="Add a sample event in step ①, then run."
      idleMessage="Run to see the output."
      error={error}
      onClearError={() => setError(null)}
      hasResult={result !== null}
    >
      {result && (
        <>
          {result.warnings.length > 0 && (
            <Alert severity="warning" sx={{ fontSize: '0.8rem', py: 0.5 }}>
              {result.warnings.map((w, i) => (
                <Typography key={i} variant="caption" display="block">{w}</Typography>
              ))}
            </Alert>
          )}

          {enrichment && (
            <>
              <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <EnrichmentIcon fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight={600}>Alert summary</Typography>
                  {enrichment.severity && (
                    <Box sx={{ ml: 'auto' }}>
                      <SeverityBadge severity={enrichment.severity} />
                    </Box>
                  )}
                </Box>
                {enrichment.title && (
                  <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                    {enrichment.title}
                  </Typography>
                )}
                {enrichment.summary && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {enrichment.summary}
                  </Typography>
                )}
              </Paper>
              {result.channels.length > 0 && <Divider />}
            </>
          )}

          {result.channels.map((ch, i) => (
            <ChannelCard key={`${ch.channel}-${i}`} result={ch} />
          ))}

          {!enrichment && result.channels.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
              No output: the rule has no enabled channels.
            </Typography>
          )}
        </>
      )}
    </PreviewRailShell>
  );
};

export default PreviewRail;
